<template>
  <div>
    <Navbar />
    <Breadcrumb />
    <div class="mx-auto container-xl mb-5">
      <div class="title-area">
        <h1 class="page-title h2 mb-2">My Teams</h1>
      </div>
    <!-- Error Display -->
    <div v-if="error" class="alert alert-danger" role="alert">
      <h4 class="alert-heading">Error Loading Dashboard</h4>
      <p>{{ error }}</p>
      <hr />
      <p class="mb-0">Please try refreshing the page or contact support if the problem persists.</p>
    </div>

    <!-- Dashboard Content - Only show when data is loaded -->
    <template v-else>
      <!-- Empty State -->
      <div
        v-if="!loading && totalSurveys === 0"
        class="alert alert-light border shadow-sm"
      >
        <h5>No team data yet</h5>
        <p class="mb-0">
          You have no completed surveys in the selected period.
          <router-link to="/create/group/survey">Create a group survey</router-link> to get started.
        </p>
      </div>

      <!-- Date Range Picker -->
      <div class="d-flex flex-wrap align-items-start mb-4" style="gap: 0.75rem;">
        <div>
          <label class="form-label fw-bold mb-1 small">Period</label>
          <div class="d-flex flex-wrap align-items-center gap-1">
            <input type="date" v-model="dateFrom" class="form-control" style="width: auto;" @change="loadData" />
            <span>→</span>
            <input type="date" v-model="dateTo" class="form-control" style="width: auto;" @change="loadData" />
          </div>
        </div>
        <div>
          <label class="form-label fw-bold mb-1 small">Rounds</label>
          <select v-model.number="rounds" class="form-select" style="width: 80px;" @change="loadData">
            <option v-for="n in [2,3,4,5]" :key="n" :value="n">{{ n }}</option>
          </select>
        </div>
        <div>
          <label class="form-label fw-bold mb-1 small">Survey Type</label>
          <select v-model.number="surveyType" class="form-select" style="width: 140px;" @change="loadData">
            <option :value="0">All</option>
            <option :value="1">GDQ Full</option>
            <option :value="2">GDQ Short</option>
          </select>
        </div>
        <div v-if="groupList.length > 0">
          <label class="form-label fw-bold mb-1 small">Teams</label>
          <div class="dropdown">
            <button class="btn btn-outline-navy btn-sm dropdown-toggle" type="button" @click="showGroupDropdown = !showGroupDropdown">
              {{ selectedGroups.length }} selected
            </button>
            <div class="dropdown-menu p-2" :class="{ show: showGroupDropdown }" style="max-height: 300px; overflow-y: auto;" @click.stop>
              <a class="dropdown-item small text-primary" href="#" @click.prevent="toggleAllGroups">{{ allGroupsSelected ? 'Unselect All' : 'Select All' }}</a>
              <div class="dropdown-divider"></div>
              <div class="form-check" v-for="g in groupList" :key="g.id">
                <input class="form-check-input" type="checkbox" :value="g.id" v-model="selectedGroups" :id="'grp_' + g.id" />
                <label class="form-check-label small" :for="'grp_' + g.id">{{ g.name || 'Group ' + g.id }}</label>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex align-self-end gap-2">
          <button class="btn btn-navy btn-sm" @click="loadData">Load</button>
          <button class="btn btn-outline-navy btn-sm" :disabled="loading" @click="exportExcel">Export to Excel</button>
        </div>
      </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="d-flex justify-content-center align-items-center py-5">
      <div class="spinner-border" style="width: 3rem; height: 3rem; color: #0a5c67;" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- KPI Cards -->
    <KpiSection v-if="!loading && kpi1">
      <KpiCard title="Teams in Stages 3–4" subtitle="Baseline → Latest" :badge="deltaBadge(kpi1, 'pp')" badge-variant="teal" :bg-color="chartColors.stage4">
        <template #value>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpi1.from }}%</span>
          <span class="text-white-50 mx-1">→</span>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpi1.to }}%</span>
        </template>
      </KpiCard>

      <KpiCard v-if="productivityApplicable && kpi2.from != null && kpi2.to != null" title="Average Productivity Percentile" subtitle="Norm benchmark" :badge="deltaBadge(kpi2, '%')" badge-variant="teal" :bg-color="chartColors.productivity">
        <template #value>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpi2.from }}</span>
          <span class="text-white-50 mx-1">→</span>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpi2.to }}</span>
        </template>
      </KpiCard>

      <KpiCard v-if="!productivityApplicable" title="Average Productivity Percentile" subtitle="GDQ Short does not measure productivity" :bg-color="chartColors.productivity" style="opacity: 0.5">
        <template #value>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">Not applicable</span>
        </template>
      </KpiCard>

      <KpiCard v-if="kpi3.from != null && kpi3.to != null" title="Average Effectiveness Percentile" subtitle="Norm benchmark" :badge="deltaBadge(kpi3, '%')" badge-variant="teal" :bg-color="chartColors.effectiveness">
        <template #value>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpi3.from }}%</span>
          <span class="text-white-50 mx-1">→</span>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpi3.to }}%</span>
        </template>
      </KpiCard>

      <KpiCard v-if="kpiAge.from != null || kpiAge.to != null" title="Average Group Age" subtitle="Months as group member" badge-variant="light" :bg-color="chartColors.stage1">
        <template #value>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpiAge.from != null ? kpiAge.from + 'm' : '-' }}</span>
          <span class="text-white-50 mx-1">→</span>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpiAge.to != null ? kpiAge.to + 'm' : '-' }}</span>
        </template>
      </KpiCard>

      <KpiCard v-if="kpiSize.from != null || kpiSize.to != null" title="Average Group Size" subtitle="Members per group" badge-variant="light" :bg-color="chartColors.stage3">
        <template #value>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpiSize.from != null ? kpiSize.from : '-' }}</span>
          <span class="text-white-50 mx-1">→</span>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ kpiSize.to != null ? kpiSize.to : '-' }}</span>
        </template>
      </KpiCard>

      <KpiCard title="Measurement Rounds" badge-variant="light" :compact="true" :bg-color="chartColors.stage2">
        <template #value>
          <span class="fw-bold text-white" style="font-size: 1.2rem;">{{ rounds }}</span>
        </template>
      </KpiCard>
    </KpiSection>

    <!-- Charts -->
    <div v-if="!loading && barTeams" class="row g-4 align-items-start">
      <div class="col-lg-6">
        <StageDistributionChart :key="rounds" :team-data="barTeams" :team-count="teamCount" :chart-max="chartMax" :rounds="rounds" />
        <div v-if="stageZeroSurveys.length > 0" class="card border-0 shadow-sm mt-2">
          <div class="card-body p-2">
            <h6 class="text-teal mb-2 small">Surveys without stage classification</h6>
            <table class="table table-sm table-borderless mb-0 small">
              <thead><tr><th class="ps-0">Survey ID</th><th>Team</th></tr></thead>
              <tbody>
                <tr v-for="s in stageZeroSurveys" :key="s.survey_id">
                  <td class="ps-0">{{ s.survey_id }}</td>
                  <td>{{ s.group_name }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="col-lg-6 d-flex flex-column gap-4">
        <ProductivityLineChart v-if="productivityApplicable" :key="rounds" :data-points="productivityData" :labels="chartLabels" />
        <EffectivenessLineChart :key="rounds" :data-points="effectivenessData" :labels="chartLabels" />
      </div>
    </div>

    </template>
    </div>
  </div>
</template>

<script>
import KPISection from './KPISection.vue'
import KPICard from './KPICard.vue'
import StageDistributionChart from './StageDistributionChart.vue'
import ProductivityLineChart from './ProductivityLineChart.vue'
import EffectivenessLineChart from './EffectivenessLineChart.vue'
import { chartColors } from '../../assets/chartColors.js'
import Navbar from '../Navbar.vue'
import Breadcrumb from '../Breadcrumb.vue'

export default {
  name: 'TeamDashboard',
  components: {
    Navbar,
    Breadcrumb,
    KpiSection: KPISection,
    KpiCard: KPICard,
    StageDistributionChart,
    ProductivityLineChart,
    EffectivenessLineChart
  },
  data() {
    const today = new Date()
    const yearStart = new Date(today.getFullYear(), 0, 1)

    return {
      chartColors,
      dateFrom: yearStart.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0],
      rounds: 3,
      surveyType: 0,
      groupList: [],
      selectedGroups: [],
      showGroupDropdown: false,
      loading: true,
      error: null,
      kpi1: null,
      kpi2: { from: null, to: null },
      kpi3: { from: null, to: null },
      kpiAge: { from: null, to: null },
      kpiSize: { from: null, to: null },
      barTeams: null,
      stageZeroSurveys: [],
      totalTeams: 0,
      productivityData: null,
      effectivenessData: null,
      surveyIds: [],
      totalSurveys: 0
    }
  },
  computed: {
    allGroupsSelected() {
      return this.groupList.length > 0 && this.selectedGroups.length === this.groupList.length
    },
    teamCount() {
      return this.groupList.length
    },
    chartMax() {
      if (!this.barTeams || !this.barTeams[0]) return 0
      const len = this.barTeams[0].length
      let max = 0
      for (let i = 0; i < len; i++) {
        let sum = 0
        for (let s = 0; s < this.barTeams.length; s++) {
          sum += this.barTeams[s][i]
        }
        if (sum > max) max = sum
      }
      return max
    },
    chartLabels() {
      const labels = []
      for (let i = 1; i <= this.rounds; i++) {
        labels.push('Round ' + i)
      }
       return labels
     },
     productivityApplicable() {
       return this.surveyType === 1
     }
   },
   watch: {
     '$store.state.user_id': async function() {
      await this.setDefaultDateRange()
      this.loadData()
    }
  },
  async created() {
    await this.setDefaultDateRange()
    const profile = this.$store.state.profile
    if (!profile) await this.$store.dispatch('profile_fetch')
    if (Number((profile || {}).teamdashboard_enabled || 0) !== 1) {
      this.$router.replace('/')
      return
    }
    this.loadData()
  },
  methods: {
    deltaBadge(kpi, unit) {
      if (!kpi || kpi.from == null || kpi.to == null) return ''
      const d = Math.round((kpi.to - kpi.from) * 10) / 10
      if (d > 0) return '↑ +' + d + ' ' + unit
      if (d < 0) return '↓ ' + d + ' ' + unit
      return '→ 0 ' + unit
    },
    toggleAllGroups() {
      if (this.allGroupsSelected) {
        this.selectedGroups = []
      } else {
        this.selectedGroups = this.groupList.map(g => g.id)
      }
    },
    async exportExcel() {
      try {
        const params = {
          scope_type: 'user',
          scope_id: this.$store.state.user_id,
          from: this.dateFrom,
          to: this.dateTo,
          rounds: this.rounds,
          survey_type: this.surveyType
        }
        if (this.selectedGroups.length > 0 && this.selectedGroups.length < this.groupList.length) {
          params.group_ids = this.selectedGroups
        }
        const resp = await this.$store.dispatch('teamdashboard_export_excel', params)
        const fileURL = window.URL.createObjectURL(new Blob([resp.data]))
        const fileLink = document.createElement('a')
        fileLink.href = fileURL
        fileLink.setAttribute('download', 'gdq_myteams_' + this.dateFrom + '-' + this.dateTo + '.xlsx')
        document.body.appendChild(fileLink)
        fileLink.click()
        fileLink.remove()
      } catch (error) {
        console.error('Failed to export Excel:', error)
        this.error = 'Failed to export Excel.'
      }
    },
    async setDefaultDateRange() {
      try {
        const r = await this.$store.dispatch('teamdashboard_surveys_fetch', {
          scope_type: 'user',
          scope_id: this.$store.state.user_id,
          from: '2000-01-01',
          to: '2099-12-31',
          rounds: 1,
          survey_type: 0
        })
        if (r.data.date_from && r.data.date_to) {
          this.dateFrom = r.data.date_from
          this.dateTo = r.data.date_to
        }
      } catch (e) {
        // keep defaults
      }
    },
    async loadData() {
      this.loading = true
      this.error = null
      this.showGroupDropdown = false
      try {
        const params = {
          scope_type: 'user',
          scope_id: this.$store.state.user_id,
          from: this.dateFrom,
          to: this.dateTo,
          rounds: this.rounds,
          survey_type: this.surveyType
        }
        if (this.selectedGroups.length > 0 && this.selectedGroups.length < this.groupList.length) {
          params.group_ids = JSON.stringify(this.selectedGroups)
        }
        const surveyResponse = await this.$store.dispatch('teamdashboard_surveys_fetch', params)

        if (surveyResponse.data.error) {
          throw new Error(surveyResponse.data.error)
        }

        // Populate group list
        if (surveyResponse.data.groups) {
          this.groupList = surveyResponse.data.groups
          if (this.selectedGroups.length === 0) {
            this.selectedGroups = this.groupList.map(g => g.id)
          }
        }
        const surveys = surveyResponse.data.surveys
        this.totalSurveys = surveys.length
        if (surveys.length === 0) {
          this.kpi1 = null
          this.kpi2 = { from: null, to: null }
          this.kpi3 = { from: null, to: null }
          this.kpiAge = { from: null, to: null }
          this.kpiSize = { from: null, to: null }
          this.productivityData = null
          this.effectivenessData = null
          this.barTeams = null
          this.stageZeroSurveys = []
          this.groupList = []
          this.selectedGroups = []
          return
        }

        // Group survey IDs by point
        const pointKeys = [...new Set(surveys.map(s => s.point))].sort((a, b) => parseInt(a) - parseInt(b))
        const byPoint = {}
        pointKeys.forEach(k => { byPoint[k] = [] })
        surveys.forEach(s => {
          if (byPoint[s.point]) byPoint[s.point].push(s.id)
        })

        // Fetch KPIs for each point
        const fetchPointKPIs = async (pointIds) => {
          const [t, p, g] = await Promise.all([
            this.$store.dispatch('teamdashboard_teams_in_stages_fetch', { survey_ids: JSON.stringify(pointIds) }),
            this.$store.dispatch('teamdashboard_percentiles_fetch', { survey_ids: JSON.stringify(pointIds) }),
            this.$store.dispatch('teamdashboard_group_stats_fetch', { survey_ids: JSON.stringify(pointIds) })
          ])
          return { teams: t.data, percentiles: p.data, groupStats: g.data }
        }

        const fetchDist = async (pointIds) => {
          const r = await this.$store.dispatch('teamdashboard_stage_distribution_fetch', {
            survey_ids: JSON.stringify(pointIds)
          })
          return r.data
        }

        const pointResults = {}
        const dists = []
        for (const k of pointKeys) {
          const [kpiRes, distRes] = await Promise.all([
            fetchPointKPIs(byPoint[k]),
            fetchDist(byPoint[k])
          ])
          pointResults[k] = kpiRes
          dists.push(distRes)
        }
        const firstKey = pointKeys[0]
        const lastKey = pointKeys[pointKeys.length - 1]

        this.kpi1 = {
          from: pointResults[firstKey].teams.pct,
          to: pointResults[lastKey].teams.pct
        }

        // KPI from/to: percentile averages from surveys at first/last point only
        const avgPct = (pctData, field) => {
          const vals = pctData.filter(p => p[field] != null).map(p => p[field])
          return vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 100) / 100 : null
        }

        this.kpi2 = {
          from: avgPct(pointResults[firstKey].percentiles, 'prod_pct'),
          to: avgPct(pointResults[lastKey].percentiles, 'prod_pct')
        }
        this.kpi3 = {
          from: avgPct(pointResults[firstKey].percentiles, 'eff_pct'),
          to: avgPct(pointResults[lastKey].percentiles, 'eff_pct')
        }
        const avgStat = (stats, field) => {
          const vals = stats.filter(s => s[field] != null).map(s => s[field])
          return vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null
        }
        this.kpiAge = {
          from: avgStat(pointResults[firstKey].groupStats, 'group_age'),
          to: avgStat(pointResults[lastKey].groupStats, 'group_age')
        }
        this.kpiSize = {
          from: avgStat(pointResults[firstKey].groupStats, 'group_size'),
          to: avgStat(pointResults[lastKey].groupStats, 'group_size')
        }

        // Per-point average percentiles (non-null only), null if all missing
        this.productivityData = pointKeys.map(k => {
          const pcts = pointResults[k].percentiles.filter(p => p.prod_pct != null).map(p => p.prod_pct)
          return pcts.length > 0 ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length * 10) / 10 : null
        })
        this.effectivenessData = pointKeys.map(k => {
          const pcts = pointResults[k].percentiles.filter(p => p.eff_pct != null).map(p => p.eff_pct)
          return pcts.length > 0 ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length * 10) / 10 : null
        })
        this.totalTeams = dists.length > 0 ? dists[0].length : 0
        this.barTeams = this.buildBarTeams(dists)
        // Collect stage 0 surveys with their IDs
        const survMap = {}
        surveys.forEach(s => { survMap[s.id] = s.group_id })
        const groupMap = {}
        this.groupList.forEach(g => { groupMap[g.id] = g.name || ('Group ' + g.id) })
        this.stageZeroSurveys = []
        if (dists.length > 0) {
          dists[0].forEach(d => {
            if (d.stage === 0) {
              const gid = survMap[d.survey_id] || 0
              this.stageZeroSurveys.push({ survey_id: d.survey_id, group_name: groupMap[gid] || 'Group ' + gid })
            }
          })
        }

      } catch (error) {
        console.error('Failed to load dashboard data:', error)
        this.error = error.message || 'Failed to load dashboard data. Please try again.'
      } finally {
        this.loading = false
      }
    },
    buildBarTeams(distributions) {
      const stages = [4, 3, 2, 1, 0]
      return stages.map(stage => {
        return distributions.map(dist => {
          const count = dist.filter(r => r.stage === stage).length
          return count
        })
      })
    }
  }
}
</script>

<style scoped>
.text-teal { color: #0a5c67; }
.bg-teal { background-color: #0a5c67; }
.btn-outline-navy {
  color: #00214F;
  border-color: #00214F;
  font-weight: 400 !important;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem !important;
}
.btn-outline-navy:hover,
.btn-outline-navy:focus,
.btn-outline-navy:active {
  color: #fff;
  background-color: #00214F;
  border-color: #00214F;
}
.btn-outline-navy:disabled {
  color: #00214F;
  border-color: #00214F;
  background-color: transparent;
}
.btn-navy {
  color: #fff;
  background-color: #00214F;
  border-color: #00214F;
  font-weight: 400 !important;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem !important;
}
.btn-navy:hover,
.btn-navy:focus,
.btn-navy:active {
  color: #fff;
  background-color: #001a3d;
  border-color: #001a3d;
}
.btn-sm { font-size: 0.875rem; }
.form-control-sm { font-size: 0.875rem; }
.form-select-sm { font-size: 0.875rem; }
.form-control, .form-select, .btn { font-size: 1rem; }
.form-label.small { font-size: 0.875rem; }

/* Restore card-body padding stripped by .container-xl .card-body { padding:0 } */
.container-xl ::v-deep .card-body.d-flex {
  padding: 1rem 1rem !important;
}
.container-xl ::v-deep .card > .card-body:not(.d-flex):not(.p-2) {
  padding: 1rem 1rem !important;
}
</style>
